import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { useClientUser } from '@/hooks';
import handleGeocode from '@/utilities/handleGeocode';
import {
    autocompleteAddressLine,
    autocompleteFullAddress,
    autocompleteLatLngPatch,
    isResolvedAutocompleteAddress,
} from '@/utilities/address';
import {
    ADD_FIRST_STORE,
    ADD_LOCATION,
    REMOVE_LOCATION,
    UPDATE_LOCATION_MAX_PALLETS,
    UPDATE_STORES,
} from '../../mutations';
import { QUERY_LOCATION_MAX_PALLETS } from '../../queries';
import {
    blockingLocations,
    chosenRecord,
    clientPayload,
    errorsFor,
    firstLocationPayload,
    locationPayload,
    maxPalletsChanges,
    mergeStores,
    setDayHours,
    storeDefaultsFor,
    storesPayload,
    storesToClearDefaultEnd,
    toggleDay,
    validationTargets,
} from './utils';

/**
 * State and mutations for the Stores panel.
 *
 * The editing model is unchanged: edits are staged locally — `clientOverrides` when the
 * "Default Hours" option is selected, `locationOverrides[idx]` when a store is — and land on an
 * explicit save. Adding a location stages it in `newStores` until the save, while removing one
 * writes immediately.
 */
export const useStores = ({ currentClient, viewOnly }) => {
    const { circles, isImposter } = useClientUser();

    const [chosenStore, setChosenStore] = useState(null);
    const [clientOverrides, setClientOverrides] = useState({});
    const [locationOverrides, setLocationOverrides] = useState([]);
    const [newStores, setNewStores] = useState([]);
    const [validationMessage, setValidationMessage] = useState('');
    // Max Pallets values whose write failed, keyed by location_id rather than list position: the
    // refetch after a save can reorder the locations.
    const [pendingMaxPallets, setPendingMaxPallets] = useState({});

    // Max Pallets is read apart from the client bootstrap queries: the column is selectable only by
    // `user` and `onward-admin`, so only the store form may depend on it. A failure here just
    // leaves the field empty; nothing else on the panel waits for it. `viewOnly` still reads it
    // because the field is shown (disabled) with its saved value.
    const hasLocations = !!currentClient?.client_id && (currentClient?.locations || []).length > 0;
    const { data: maxPalletsData, refetch: refetchMaxPallets } = useQuery(QUERY_LOCATION_MAX_PALLETS, {
        variables: { client_id: currentClient?.client_id },
        skip: !hasLocations,
        fetchPolicy: 'cache-and-network',
        onError: (error) => {
            console.error(error);
            Sentry.captureException(error);
        },
    });

    const maxPallets = useMemo(
        () =>
            Object.fromEntries(
                (maxPalletsData?.locations || []).map((row) => [row.location_id, row.warehouse_max_pallets])
            ),
        [maxPalletsData]
    );

    const refreshMaxPallets = () =>
        hasLocations ? Promise.resolve(refetchMaxPallets?.()).catch(() => {}) : Promise.resolve();

    const stores = useMemo(
        () =>
            mergeStores({
                locations: currentClient?.locations,
                newStores,
                locationOverrides,
                maxPallets,
                pendingMaxPallets,
            }),
        [currentClient, newStores, locationOverrides, maxPallets, pendingMaxPallets]
    );

    const storeDefaults = useMemo(() => storeDefaultsFor(currentClient), [currentClient]);

    const chosen = useMemo(
        () => chosenRecord({ storeDefaults, stores, chosenStore, clientOverrides }),
        [storeDefaults, stores, chosenStore, clientOverrides]
    );

    const isFirstLocation = stores.length === 0;

    // Field-level highlighting follows the record on screen.
    const hasError = useMemo(
        () => errorsFor({ chosenStore, chosen, isFirstLocation }),
        [chosenStore, chosen, isFirstLocation]
    );

    // The bulk save is judged on every location this session edited — see validationTargets.
    const blocking = useMemo(() => {
        const targets = validationTargets({
            storeCount: stores.length,
            locationOverrides,
            savedCount: (currentClient?.locations || []).length,
        });
        return blockingLocations({ stores, targets });
    }, [stores, locationOverrides, currentClient]);

    const onMutationError = (message) => (error) => {
        console.error(error);
        if (message) setValidationMessage(message);
        Sentry.captureException(error);
    };

    // Evicting the client makes Apollo refetch its locations, which is how saved rows appear.
    const evictClient = (cache) => cache.evict(cache.identify(currentClient));

    const [addLocation] = useMutation(ADD_LOCATION, {
        update: evictClient,
        onError: onMutationError(),
        onCompleted: () => {
            setNewStores([]);
            refreshMaxPallets();
        },
    });

    // Written after the main save, and only for locations the user changed. It fails on its own:
    // the store save has already landed by then.
    const [updateMaxPallets] = useMutation(UPDATE_LOCATION_MAX_PALLETS, {
        onError: onMutationError(),
    });

    const [removeLocation] = useMutation(REMOVE_LOCATION, {
        update: (cache, { data }) => cache.evict(cache.identify(data?.delete_locations_by_pk)),
        onError: onMutationError(),
    });

    const [updateClient] = useMutation(UPDATE_STORES, {
        update: evictClient,
        onError: onMutationError('There was an error updating your location. Please try again.'),
    });

    const [addFirstStore] = useMutation(ADD_FIRST_STORE, {
        update: evictClient,
        onError: onMutationError(),
        onCompleted: () => {
            setNewStores([]);
            setLocationOverrides([]);
            setClientOverrides({});
            refreshMaxPallets();
        },
    });

    /**
     * Route an edit to whichever record is selected. This is the seam the whole form writes
     * through, so a store edit can never leak onto the client and vice versa.
     */
    const setOverrides = useCallback(
        (callback) => {
            if (Number.isInteger(chosenStore)) {
                setLocationOverrides((prev) => {
                    const clone = [...prev];
                    clone[chosenStore] = callback(clone[chosenStore] || {});
                    return clone;
                });
            } else {
                setClientOverrides(callback);
            }
        },
        [chosenStore]
    );

    const patch = useCallback((fields) => setOverrides((prev) => ({ ...prev, ...fields })), [setOverrides]);

    const selectStore = (value) => setChosenStore(value === null ? null : parseInt(value, 10));

    const addNewLocation = () => {
        setNewStores([{ ...storeDefaults, location_name: 'New Location', client_id: currentClient.client_id }]);
        setChosenStore((currentClient?.locations || []).length);
    };

    /** Discards an added-but-unsaved location and returns to the default-hours view. */
    const discardNewLocation = () => {
        setNewStores([]);
        setChosenStore(null);
    };

    const removeChosenLocation = () => {
        const location_id = stores?.[chosenStore]?.location_id;
        removeLocation({ variables: { location_id } });
    };

    const submitLocation = useCallback(
        () =>
            addLocation({
                variables: { locations_input_object: locationPayload({ chosen }) },
            }),
        [chosen, addLocation]
    );

    const submitFirstLocation = useCallback(
        () =>
            addFirstStore({
                variables: firstLocationPayload({
                    client: currentClient,
                    chosen,
                    clientOverrides,
                }),
            }),
        [currentClient, chosen, clientOverrides, addFirstStore]
    );

    const saveClient = useCallback(async () => {
        const changes = maxPalletsChanges({
            stores,
            locationOverrides,
            savedCount: (currentClient?.locations || []).length,
            pendingMaxPallets,
        });
        const result = await updateClient({
            variables: {
                client_id: currentClient.client_id,
                client: clientPayload({ client: currentClient, clientOverrides }),
                locations: storesPayload({ stores }),
            },
        });
        // The store save failed: keep every edit staged and let onError speak.
        if (!result?.data) return;

        const writes = await Promise.all(
            changes.map(({ location_id, warehouse_max_pallets }) =>
                updateMaxPallets({ variables: { location_id, warehouse_max_pallets } })
            )
        );
        const failed = changes.filter((_, i) => !writes[i]?.data);

        // Wait for the fresh values before dropping the staged edit, so the field never flashes the
        // old saved value. A value whose write failed stays pending by location_id, so it needn't be
        // retyped and lands on the same location even if the refetch reorders the list.
        if (changes.length) await refreshMaxPallets();
        setPendingMaxPallets(
            Object.fromEntries(
                failed.map(({ location_id, warehouse_max_pallets }) => [location_id, warehouse_max_pallets])
            )
        );
        setLocationOverrides([]);
        setClientOverrides({});
        setValidationMessage(
            failed.length
                ? 'Locations saved, but Max Pallets could not be updated. Please try again.'
                : 'Location updated successfully!'
        );
    }, [currentClient, clientOverrides, stores, locationOverrides, pendingMaxPallets, updateClient, updateMaxPallets]);

    const toggleDayOpen = (idx) => patch(toggleDay(chosen.hours_open, idx));

    const changeDayHours = (idx, edge, value) => patch(setDayHours(chosen.hours_open, idx, edge, value));

    const toggleHoliday = (field) => setOverrides((prev) => ({ ...prev, [field]: !prev[field] }));

    /**
     * `default_end_location` is exclusive across a client's stores, so toggling it on here clears
     * every other store that holds it — in the same state update, so the save sees all of them.
     * A toggle rather than a setter, matching the control it replaced.
     */
    const toggleDefaultEndLocation = () => {
        const toClear = storesToClearDefaultEnd(stores, chosenStore);
        const next = !chosen?.default_end_location;
        setLocationOverrides((prev) => {
            const clone = [...prev];
            toClear.forEach((idx) => {
                clone[idx] = { ...(clone[idx] || {}), default_end_location: false };
            });
            if (Number.isInteger(chosenStore)) {
                clone[chosenStore] = { ...(clone[chosenStore] || {}), default_end_location: next };
            }
            return clone;
        });
    };

    const updateAddress = async (address, split) => {
        if (!address) return;

        if (isResolvedAutocompleteAddress(split)) {
            patch({
                business_address: autocompleteAddressLine(address, split),
                business_city: split.city,
                business_state: split.state,
                business_zip: split.zip,
                ...autocompleteLatLngPatch(split, 'lat', 'lng'),
            });
            return;
        }

        try {
            const { street, state, city, zip, lat, lng } = await handleGeocode({
                address: autocompleteFullAddress(address, split),
            });
            patch({
                business_address: street,
                business_city: city,
                business_state: state,
                business_zip: zip,
                lat,
                lng,
            });
        } catch (e) {
            console.error(e);
            Sentry.captureException(e);
        }
    };

    return {
        state: {
            stores,
            chosenStore,
            chosen,
            hasError,
            validationMessage,
            viewOnly: !!viewOnly,
            isImposter,
            // `saas-v1` gates the Default End Location control, as before.
            showDefaultEndLocation: !!(circles && circles['saas-v1']),
            isFirstLocation,
            blocking,
            // The visible form must be complete before its own submit is offered...
            canSubmitSelected: Object.keys(hasError).length === 0,
            // ...and the bulk save additionally needs every touched location to be complete.
            canSaveChanges: blocking.length === 0,
            // An added-but-unsaved location gets its own Submit/Close pair: it is inserted with
            // ADD_LOCATION rather than folded into the bulk client save.
            isNewLocationSelected: newStores.length > 0 && chosenStore === stores.length - 1,
        },
        callbacks: {
            selectStore,
            addNewLocation,
            removeChosenLocation,
            discardNewLocation,
            patch,
            submitLocation,
            submitFirstLocation,
            saveClient,
            toggleDayOpen,
            changeDayHours,
            toggleHoliday,
            toggleDefaultEndLocation,
            updateAddress,
        },
    };
};
